import { Button, Flex } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { GroupContainer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/TransactionDetails/styled';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Documents } from '@dealstack/frontend/shared/components/Documents/Documents';
import { DocumentWrapper } from './../styled';
import { DocumentDrawer } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/DocumentDrawer';
import { UploadEntityDetails } from '@dealstack/frontend/shared/components/Documents/types';
import { useCallback, useMemo, useState } from 'react';
import { EditDocument } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/types';
import { useRemoveDocument } from '@dealstack/frontend/shared/components/Documents/hooks/useRemoveDocument';
import { usePreviewFile } from '@dealstack/frontend/shared/components/Documents/hooks/usePreviewFile';
import { useDownloadFileUsingFileStorageUrl } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadFileUsingFileStorageUrl';
import { useDocumentListColumns } from '@dealstack/frontend/shared/components/Documents/hooks/useDocumentListColumns';
import {
	TableOperations,
	useMantineTableWithPaginationFilteringSorting,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { useDocuments } from '@dealstack/frontend/shared/components/Documents/hooks/useDocuments';
import {
	DocumentFieldFilter,
	DocumentLinkTarget,
	DocumentsRes,
} from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { Row } from '@tanstack/react-table';
import { DEFAULT_DOCUMENTS_TABLE_OPERATIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/constants';
import { hasFilterApplied } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/hasFilterApplied';
import { hasSearchApplied } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/hasSearchApplied';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	transactionId: string;
	uploadEntityDetails?: UploadEntityDetails[];
}

export const DocumentSection = ({ uploadEntityDetails, transactionId }: Props) => {
	const tCapManage = useTranslationCapManage();
	const tCommon = useTranslationCommonUi();
	const { dealId } = useRequiredParams('dealId');
	const [isUploadActive, setIsUploadActive] = useState<boolean>(false);
	const [hasDocuments, setHasDocuments] = useState<boolean>(false);
	const [editDocument, setEditDocument] = useState<EditDocument>({ documentId: '' });

	// This section is always scoped to a transaction.
	const target = useMemo<DocumentLinkTarget>(() => ({ transactionId }), [transactionId]);

	const [classifierFilters, setClassifierFilters] = useState<string[]>([]);

	const { downloadFile } = useDownloadFileUsingFileStorageUrl();
	const { previewFile, previewDocument, setPreviewDocument } = usePreviewFile();
	const { removeDocumentLink } = useRemoveDocument();

	const handleRemoveDocument = useCallback(
		(row: Row<DocumentsRes>) => {
			void removeDocumentLink({
				documentId: row.original.documentId,
				dealId,
				transactionId,
			});
		},
		[removeDocumentLink, transactionId, dealId]
	);

	const columns = useDocumentListColumns({
		tCapManage,
		tCommon,
		classifierFilters,
		onRowClick: (row) =>
			setEditDocument({
				documentId: row.original.documentId,
				target,
			}),
		downloadFile: (fileId: string, dealId: string, fileName: string) =>
			downloadFile({ fileId, dealId, fileName }),
		previewFile: (document: DocumentsRes, dealId: string) => previewFile(document, dealId),
		removeDocument: handleRemoveDocument,
		dealId,
		targetId: transactionId,
	});

	const { mantineTableDataOperations, dataOperations } =
		useMantineTableWithPaginationFilteringSorting(columns, DEFAULT_DOCUMENTS_TABLE_OPERATIONS);

	const hasFilterAppliedValue = hasFilterApplied(dataOperations);
	const hasSearchAppliedValue = hasSearchApplied(dataOperations);

	const { data: documentsData, isFetching: isDocumentsLoading } = useDocuments({
		dealId,
		target,
		dataOperations: dataOperations as TableOperations<DocumentFieldFilter>,
		setClassifierFilters,
		setHasDocuments,
	});

	// TODO check user has permissions to view this page

	return (
		<Flex direction="column" h="100%">
			<GroupContainer>
				{!isUploadActive && (
					<Button
						data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_DOCUMENTS_ADD_NEW_BUTTON}
						leftSection={<Icon variant="upload-v3-thin" size="lg" />}
						variant="default"
						onClick={() => setIsUploadActive(true)}
					>
						{tCapManage('documents.buttons.add-new')}
					</Button>
				)}
				{isUploadActive && hasDocuments && (
					<Button
						leftSection={<Icon variant="reload" size="lg" />}
						variant="default"
						onClick={() => setIsUploadActive(false)}
					>
						{tCapManage('documents.buttons.go-back')}
					</Button>
				)}
			</GroupContainer>
			<DocumentWrapper>
				<Documents
					dealId={dealId}
					target={target}
					uploadEntityDetails={uploadEntityDetails}
					setIsUploadActive={setIsUploadActive}
					isUploadActive={isUploadActive}
					setEditDocument={setEditDocument}
					showFilters={true}
					columns={columns}
					mantineTableDataOperations={mantineTableDataOperations}
					documents={documentsData?.payload}
					isDocumentsLoading={isDocumentsLoading}
					hasFilterApplied={hasFilterAppliedValue}
					hasSearchApplied={hasSearchAppliedValue}
					previewDocument={previewDocument}
					setPreviewDocument={setPreviewDocument}
				/>
			</DocumentWrapper>
			<DocumentDrawer
				editDocument={editDocument}
				setEditDocument={setEditDocument}
				dataOperations={dataOperations as TableOperations<DocumentFieldFilter>}
			/>
		</Flex>
	);
};
